import ExcelJS from "exceljs";
import dayjs from "dayjs";
import apiService from "../services/api";
import { withOwnerUserId, withStrictOwnerOnly } from "./ownerScopeParams";

/**
 * 普通用户导出固定为本人；管理员导出固定为全平台。
 */
function resolveStoredCurrentUserId() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = Number(parsed?.id);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function resolvePanoramaExportOwnerUserId(isAdmin, adminActiveKeys) {
  void adminActiveKeys;
  if (isAdmin) return null;
  return resolveStoredCurrentUserId();
}

/**
 * 导出数据范围与分类展示是两个独立口径：
 * - 指定 ownerUserId 时严格限制为该用户显式归属的账号；
 * - 无论用户角色如何，都保留这些账号中尚未配置业务线/策略类型的数据，并归入「未分配」。
 */
export function resolvePanoramaExportDataScope(ownerUserId) {
  return {
    strictOwnerOnly: ownerUserId != null,
    includeUnassignedClassified: true,
  };
}

export function sanitizeSheetName(name) {
  let s = String(name).replace(/[\[\]*?:/\\]/g, "_").trim() || "Sheet";
  if (s.length > 31) s = s.slice(0, 31);
  return s;
}

export function filterClassifiedDimensionRows(rows, includeUnassigned = true) {
  const list = Array.isArray(rows) ? rows : [];
  if (includeUnassigned) return list;
  return list.filter((row) => Number(row?.id ?? 0) > 0);
}

function ensureUniqueSheetName(workbook, base) {
  let name = sanitizeSheetName(base);
  let i = 2;
  while (workbook.getWorksheet(name)) {
    const suffix = `_${i}`;
    const max = 31 - suffix.length;
    name = sanitizeSheetName(`${String(base).slice(0, Math.max(1, max))}${suffix}`);
    i += 1;
  }
  return name;
}

async function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function addMetaSheet(wb, { pageTitle, dateFrom, dateTo, ownerUserId }) {
  const name = ensureUniqueSheetName(wb, "元信息");
  const ws = wb.addWorksheet(name);
  const rows = [
    ["页面", pageTitle],
    ["起始日期", dateFrom],
    ["结束日期", dateTo],
    ["数据范围", ownerUserId == null ? "全平台汇总" : `用户 ID ${ownerUserId}`],
    ["导出时间", dayjs().format("YYYY-MM-DD HH:mm:ss")],
  ];
  rows.forEach((r, i) => {
    ws.getRow(i + 1).values = r;
  });
}

/**
 * 对比口径：当前区间的起止日期统一向前平移 7 天，区间长度保持不变。
 * 例如当前 5/11~5/12，对比区间为 5/4~5/5；当前 5/11 单日，对比区间为 5/4。
 */
export function computeEqualLengthPreviousRange(dateFromStr, dateToStr) {
  const from = dayjs(dateFromStr);
  const to = dayjs(dateToStr);
  if (!from.isValid() || !to.isValid() || to.isBefore(from)) {
    return null;
  }
  const prevFrom = from.subtract(7, "day");
  const prevTo = to.subtract(7, "day");
  return {
    prevFrom: prevFrom.format("YYYY-MM-DD"),
    prevTo: prevTo.format("YYYY-MM-DD"),
  };
}

function sumTotalPnlFromEquityTrends(rows) {
  return (rows || []).reduce((s, r) => s + Number(r.total_pnl ?? 0), 0);
}

async function fetchPeriodSummaryMetrics(dateFrom, dateTo, ownerUserId, strictOwnerOnly = false) {
  const params = withStrictOwnerOnly(
    withOwnerUserId({ date_from: dateFrom, date_to: dateTo }, ownerUserId),
    strictOwnerOnly,
  );
  const [statsRes, eqRes] = await Promise.all([
    apiService.trades.getTradeStats(params),
    apiService.equity.getTotalTrends(params),
  ]);
  const st = statsRes.data?.data || {};
  const vol = Number(st.total_volume || 0);
  const makerVol = Number(st.total_maker_volume || 0);
  const fee = Number(st.total_fee || 0);
  const trends = eqRes.data?.data || [];
  const pnl = sumTotalPnlFromEquityTrends(trends);
  const makerRatioPct = vol > 0 ? (makerVol / vol) * 100 : 0;
  // 与报告 ROT 一致：盈亏 / 成交量 × 10000（bps）
  const rotBps = vol > 0 ? (pnl / vol) * 10000 : 0;
  return {
    totalVolume: vol,
    totalPnl: pnl,
    rotBps,
    // 与交易量统计接口 total_fee 一致：区间手续费合计（USDT 等值）
    totalFee: fee,
    makerRatioPct,
  };
}

/**
 * 按交易所汇总：成交量（来自 trades/stats/exchanges 按交易所加总）；
 * 盈亏（各交易所+交易类型下权益日 pnl 之和再按交易所加总）.
 */
async function fetchExchangeAggregates(dateFrom, dateTo, ownerUserId, strictOwnerOnly = false) {
  const params = withStrictOwnerOnly(
    withOwnerUserId(
      { date_from: dateFrom, date_to: dateTo, limit: 500 },
      ownerUserId,
    ),
    strictOwnerOnly,
  );
  const [stRes, eqRes] = await Promise.all([
    apiService.trades.getExchangeStats(params),
    apiService.equity.getExchangeTrends(params),
  ]);
  const exStats = stRes.data?.data || [];
  const exEq = eqRes.data?.data || [];
  const byEx = new Map();
  for (const row of exStats) {
    const k = String(row.exchange || "").trim() || "(未知)";
    if (!byEx.has(k)) byEx.set(k, { exchange: k, volume: 0, pnl: 0 });
    byEx.get(k).volume += Number(row.total_volume || 0);
  }
  for (const g of exEq) {
    const k = String(g.exchange || "").trim() || "(未知)";
    const pnlSum = (g.trends || []).reduce((s, t) => s + Number(t.pnl ?? 0), 0);
    if (!byEx.has(k)) byEx.set(k, { exchange: k, volume: 0, pnl: 0 });
    byEx.get(k).pnl += pnlSum;
  }
  return byEx;
}

const EXCHANGE_TRADE_TYPE_KEY_SEP = "\u0001";

/**
 * 按「交易所 + 交易类型」汇总：成交量来自 stats/exchanges（已按组返回）；盈亏来自权益分交易所走势（每组含 trade_type）.
 */
async function fetchExchangeTradeTypeAggregates(dateFrom, dateTo, ownerUserId, strictOwnerOnly = false) {
  const params = withStrictOwnerOnly(
    withOwnerUserId(
      { date_from: dateFrom, date_to: dateTo, limit: 500 },
      ownerUserId,
    ),
    strictOwnerOnly,
  );
  const [stRes, eqRes] = await Promise.all([
    apiService.trades.getExchangeStats(params),
    apiService.equity.getExchangeTrends(params),
  ]);
  const byKey = new Map();
  for (const row of stRes.data?.data || []) {
    const ex = String(row.exchange || "").trim() || "(未知)";
    const tt = String(row.trade_type || "").trim() || "(未知)";
    const k = `${ex}${EXCHANGE_TRADE_TYPE_KEY_SEP}${tt}`;
    if (!byKey.has(k)) byKey.set(k, { exchange: ex, tradeType: tt, volume: 0, pnl: 0 });
    byKey.get(k).volume += Number(row.total_volume || 0);
  }
  for (const g of eqRes.data?.data || []) {
    const ex = String(g.exchange || "").trim() || "(未知)";
    const tt = String(g.trade_type || "").trim() || "(未知)";
    const k = `${ex}${EXCHANGE_TRADE_TYPE_KEY_SEP}${tt}`;
    const pnlSum = (g.trends || []).reduce((s, t) => s + Number(t.pnl ?? 0), 0);
    if (!byKey.has(k)) byKey.set(k, { exchange: ex, tradeType: tt, volume: 0, pnl: 0 });
    byKey.get(k).pnl += pnlSum;
  }
  return byKey;
}

/**
 * 按业务线或策略类型汇总：成交量来自 trades/stats/classified；盈亏来自权益对应维度走势.
 * tradeType 为 "spot" / "swap" 时仅统计该类型；留空（默认）合并全部。
 */
async function fetchClassifiedAggregates(
  dimension,
  dateFrom,
  dateTo,
  ownerUserId,
  includeUnassignedClassified = true,
  strictOwnerOnly = false,
  tradeType = "",
) {
  const base = withStrictOwnerOnly(
    withOwnerUserId({ date_from: dateFrom, date_to: dateTo, limit: 500 }, ownerUserId),
    strictOwnerOnly,
  );
  const tradeParams = { ...base, dimension };
  const eqParams = { ...base };
  if (tradeType) {
    tradeParams.trade_type = tradeType;
    eqParams.trade_type = tradeType;
  }
  const [stRes, eqRes] = await Promise.all([
    apiService.trades.getClassifiedTradeStats(tradeParams),
    dimension === "business_line"
      ? apiService.equity.getBusinessLineTrends(eqParams)
      : apiService.equity.getStrategyTypeTrends(eqParams),
  ]);
  const byKey = new Map();
  const tradeRows = filterClassifiedDimensionRows(
    stRes.data?.data || [],
    includeUnassignedClassified,
  );
  const equityRows = filterClassifiedDimensionRows(
    eqRes.data?.data || [],
    includeUnassignedClassified,
  );
  for (const row of tradeRows) {
    const id = Number(row.id ?? 0);
    const k = String(id);
    const name = String(row.name ?? "").trim() || "未分配";
    const code = String(row.code ?? "").trim();
    if (!byKey.has(k)) byKey.set(k, { id, name, code, volume: 0, pnl: 0 });
    byKey.get(k).volume += Number(row.total_volume || 0);
  }
  for (const g of equityRows) {
    const id = Number(g.id ?? 0);
    const k = String(id);
    const name = String(g.name ?? "").trim() || "未分配";
    const code = String(g.code ?? "").trim();
    const pnlSum = (g.trends || []).reduce((s, t) => s + Number(t.pnl ?? 0), 0);
    if (!byKey.has(k)) byKey.set(k, { id, name, code, volume: 0, pnl: 0 });
    const e = byKey.get(k);
    e.pnl += pnlSum;
    if (name && e.name === "未分配" && id !== 0) e.name = name;
    if (code && !e.code) e.code = code;
  }
  return byKey;
}

/** 按业务线或策略类型 + (spot/swap) 拆分聚合：返回 Map<id, { id, name, code, spot:{volume,pnl}, swap:{volume,pnl} }>。 */
async function fetchClassifiedAggregatesByTradeType(
  dimension,
  dateFrom,
  dateTo,
  ownerUserId,
  includeUnassignedClassified = true,
  strictOwnerOnly = false,
) {
  const [spotMap, swapMap] = await Promise.all([
    fetchClassifiedAggregates(
      dimension,
      dateFrom,
      dateTo,
      ownerUserId,
      includeUnassignedClassified,
      strictOwnerOnly,
      "spot",
    ),
    fetchClassifiedAggregates(
      dimension,
      dateFrom,
      dateTo,
      ownerUserId,
      includeUnassignedClassified,
      strictOwnerOnly,
      "swap",
    ),
  ]);
  const merged = new Map();
  const upsert = (k, src, tradeType) => {
    if (!merged.has(k)) {
      merged.set(k, {
        id: src.id,
        name: src.name,
        code: src.code,
        spot: { volume: 0, pnl: 0 },
        swap: { volume: 0, pnl: 0 },
      });
    }
    const e = merged.get(k);
    e[tradeType] = { volume: Number(src.volume || 0), pnl: Number(src.pnl || 0) };
    if (src.name && e.name === "未分配" && src.id !== 0) e.name = src.name;
    if (src.code && !e.code) e.code = src.code;
  };
  for (const [k, v] of spotMap.entries()) upsert(k, v, "spot");
  for (const [k, v] of swapMap.entries()) upsert(k, v, "swap");
  return merged;
}

function sumAggregateMap(metricsMap) {
  const total = { volume: 0, pnl: 0 };
  for (const row of metricsMap?.values?.() || []) {
    if (row?.spot || row?.swap) {
      total.volume += Number(row.spot?.volume || 0) + Number(row.swap?.volume || 0);
      total.pnl += Number(row.spot?.pnl || 0) + Number(row.swap?.pnl || 0);
      continue;
    }
    total.volume += Number(row?.volume || 0);
    total.pnl += Number(row?.pnl || 0);
  }
  return total;
}

/**
 * 生成业务线/策略类型总表行：每个维度先输出与页面走势图一致的「合计」，再输出现货/合约拆解。
 * 合计行只用于展示与对账，跨维度校验仍直接汇总原始 Map，避免重复计数。
 */
export function buildClassifiedBreakdownRows(curMap = new Map(), prevMap = new Map()) {
  const tradeTypes = ["spot", "swap"];
  const emptyBucket = () => ({ volume: 0, pnl: 0 });
  const readBucket = (entry, tradeType) => ({
    volume: Number(entry?.[tradeType]?.volume || 0),
    pnl: Number(entry?.[tradeType]?.pnl || 0),
  });
  const sumEntry = (entry) =>
    tradeTypes.reduce(
      (total, tradeType) => {
        const bucket = readBucket(entry, tradeType);
        total.volume += bucket.volume;
        total.pnl += bucket.pnl;
        return total;
      },
      emptyBucket(),
    );
  const isDoubleZero = (current, previous) =>
    current.volume === 0 &&
    current.pnl === 0 &&
    previous.volume === 0 &&
    previous.pnl === 0;

  const keys = [...new Set([...curMap.keys(), ...prevMap.keys()])].sort((a, b) => {
    const volumeA = sumEntry(curMap.get(a)).volume;
    const volumeB = sumEntry(curMap.get(b)).volume;
    if (volumeB !== volumeA) return volumeB - volumeA;
    const nameA = curMap.get(a)?.name || prevMap.get(a)?.name || "未分配";
    const nameB = curMap.get(b)?.name || prevMap.get(b)?.name || "未分配";
    return String(nameA).localeCompare(String(nameB), "zh-CN");
  });

  const rows = [];
  for (const key of keys) {
    const currentEntry = curMap.get(key);
    const previousEntry = prevMap.get(key);
    const name = currentEntry?.name || previousEntry?.name || "未分配";
    const currentTotal = sumEntry(currentEntry);
    const previousTotal = sumEntry(previousEntry);
    if (!isDoubleZero(currentTotal, previousTotal)) {
      rows.push({
        key,
        name,
        tradeType: "total",
        current: currentTotal,
        previous: previousTotal,
        isTotal: true,
      });
    }
    for (const tradeType of tradeTypes) {
      const current = readBucket(currentEntry, tradeType);
      const previous = readBucket(previousEntry, tradeType);
      if (isDoubleZero(current, previous)) continue;
      rows.push({
        key,
        name,
        tradeType,
        current,
        previous,
        isTotal: false,
      });
    }
  }
  return rows;
}

/** 构建总表的跨维度对账结果；差额容许 1 分钱以内的浮点误差。 */
export function buildPanoramaReconciliationRows({
  core,
  exchange,
  exchangeTradeType,
  businessLine,
  strategyType,
}) {
  const tolerance = 0.011;
  const groups = [
    ["按交易所", exchange],
    ["按交易所+交易类型", exchangeTradeType],
    ["按业务线（含未分配）", businessLine],
    ["按策略类型（含未分配）", strategyType],
  ];
  return groups.map(([label, metricsMap]) => {
    const total = sumAggregateMap(metricsMap);
    const volumeDiff = total.volume - Number(core?.totalVolume || 0);
    const pnlDiff = total.pnl - Number(core?.totalPnl || 0);
    return {
      label,
      volume: total.volume,
      volumeDiff,
      pnl: total.pnl,
      pnlDiff,
      passed: Math.abs(volumeDiff) <= tolerance && Math.abs(pnlDiff) <= tolerance,
    };
  });
}

function applySummarySheetLayout(ws) {
  ws.views = [{ state: "frozen", ySplit: 3 }];
  [28, 22, 22, 22, 20, 22, 20, 22, 20, 20].forEach((width, index) => {
    ws.getColumn(index + 1).width = width;
  });
  ws.eachRow((row) => {
    const first = String(row.getCell(1).value || "");
    if (row.actualCellCount === 1 && first) {
      row.getCell(1).font = { bold: true };
    }
  });
}

/**
 * 总表：① 全平台核心指标 + 7 天前同期；② 按交易所（类型合并）；③ 按交易所+交易类型；④ 按业务线；⑤ 按策略类型。
 */
async function addExportSummarySheet(
  wb,
  {
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified = true,
    strictOwnerOnly = ownerUserId != null,
  },
) {
  const range = computeEqualLengthPreviousRange(dateFrom, dateTo);
  const name = ensureUniqueSheetName(wb, "总表");
  const ws = wb.addWorksheet(name);

  const prevPromise = range
    ? fetchPeriodSummaryMetrics(range.prevFrom, range.prevTo, ownerUserId, strictOwnerOnly)
    : Promise.resolve(null);
  const prevExPromise = range
    ? fetchExchangeAggregates(range.prevFrom, range.prevTo, ownerUserId, strictOwnerOnly)
    : Promise.resolve(new Map());
  const prevExTtPromise = range
    ? fetchExchangeTradeTypeAggregates(range.prevFrom, range.prevTo, ownerUserId, strictOwnerOnly)
    : Promise.resolve(new Map());
  const prevBlPromise = range
    ? fetchClassifiedAggregatesByTradeType(
        "business_line",
        range.prevFrom,
        range.prevTo,
        ownerUserId,
        includeUnassignedClassified,
        strictOwnerOnly,
      )
    : Promise.resolve(new Map());
  const prevStPromise = range
    ? fetchClassifiedAggregatesByTradeType(
        "strategy_type",
        range.prevFrom,
        range.prevTo,
        ownerUserId,
        includeUnassignedClassified,
        strictOwnerOnly,
      )
    : Promise.resolve(new Map());

  const [cur, prev, curEx, prevEx, curExTt, prevExTt, curBl, prevBl, curSt, prevSt] =
    await Promise.all([
      fetchPeriodSummaryMetrics(dateFrom, dateTo, ownerUserId, strictOwnerOnly),
      prevPromise,
      fetchExchangeAggregates(dateFrom, dateTo, ownerUserId, strictOwnerOnly),
      prevExPromise,
      fetchExchangeTradeTypeAggregates(dateFrom, dateTo, ownerUserId, strictOwnerOnly),
      prevExTtPromise,
      fetchClassifiedAggregatesByTradeType(
        "business_line",
        dateFrom,
        dateTo,
        ownerUserId,
        includeUnassignedClassified,
        strictOwnerOnly,
      ),
      prevBlPromise,
      fetchClassifiedAggregatesByTradeType(
        "strategy_type",
        dateFrom,
        dateTo,
        ownerUserId,
        includeUnassignedClassified,
        strictOwnerOnly,
      ),
      prevStPromise,
    ]);

  ws.addRow(["总表：当期数据 + 7天前同期对比"]);
  ws.addRow(["当前区间", `${dateFrom} ~ ${dateTo}`]);
  ws.addRow([
    "对比区间（7天前同期）",
    range ? `${range.prevFrom} ~ ${range.prevTo}` : "—",
  ]);

  /** 千分位 + 两位小数 */
  const fmtThousands2 = "#,##0.00";
  const fmtDec2 = "0.00";
  /** Excel 百分比：单元格存小数（1 = 100%），格式 0.00% 显示百分号 */
  const fmtPct = "0.00%";
  /** 较 7 天前变化：数值单元格（存小数），正数带 + 号，便于在 Excel 内直接用公式核对 */
  const fmtPctSigned = "+0.00%;-0.00%;0.00%";

  const hasPrev = !!(range && prev);
  const pct = (a, b) => {
    if (!hasPrev) return "—";
    const c = Number(a);
    const p = Number(b);
    if (Number.isNaN(c)) return "—";
    if (Number.isNaN(p) || p === 0) {
      return c === 0 ? { v: 0, fmt: fmtPctSigned } : "—（对比区间无基数）";
    }
    return { v: (c - p) / Math.abs(p), fmt: fmtPctSigned };
  };

  /** trade_type 中文标签：与"按业务线 / 策略类型"块保持一致。未知 trade_type 原样显示。 */
  const TRADE_TYPE_LABELS = { spot: "现货", swap: "合约" };
  const tradeTypeLabel = (tt) => TRADE_TYPE_LABELS[tt] || tt || "";
  // ---------- 共享：维度排序（按当期成交量降序） ----------
  const sortedExchanges = [...new Set([...curEx.keys(), ...prevEx.keys()])].sort((a, b) => {
    const va = curEx.get(a)?.volume ?? 0;
    const vb = curEx.get(b)?.volume ?? 0;
    if (vb !== va) return vb - va;
    return String(a).localeCompare(String(b), "zh-CN");
  });
  const sortedExTt = [...new Set([...curExTt.keys(), ...prevExTt.keys()])].sort((a, b) => {
    const va = curExTt.get(a)?.volume ?? 0;
    const vb = curExTt.get(b)?.volume ?? 0;
    if (vb !== va) return vb - va;
    const ia = a.indexOf(EXCHANGE_TRADE_TYPE_KEY_SEP);
    const ib = b.indexOf(EXCHANGE_TRADE_TYPE_KEY_SEP);
    const ea = ia >= 0 ? a.slice(0, ia) : a;
    const ta = ia >= 0 ? a.slice(ia + 1) : "";
    const eb = ib >= 0 ? b.slice(0, ib) : b;
    const tb = ib >= 0 ? b.slice(ib + 1) : "";
    const c = String(ea).localeCompare(String(eb), "zh-CN");
    if (c !== 0) return c;
    return String(ta).localeCompare(String(tb), "zh-CN");
  });
  /**
   * 通用：把一行单元格定义写入工作表。cells 元素可为原始值，或 { v, fmt } 携带数字格式。
   * 这样每个块只需声明「先当期列、后 7 天前同期列」的有序数组，由本函数统一落格式。
   */
  const addFmtRow = (cells) => {
    const row = ws.addRow(
      cells.map((c) => (c && typeof c === "object" && "v" in c ? c.v : c)),
    );
    cells.forEach((c, i) => {
      if (c && typeof c === "object" && c.fmt) {
        const cell = row.getCell(i + 1);
        if (cell.value != null && cell.value !== "") cell.numFmt = c.fmt;
      }
    });
    return row;
  };
  const num2 = (v, fmt) => ({ v: Number(Number(v).toFixed(2)), fmt });
  /** 对比尾列：7 天前同期成交量、变化率、盈亏、变化率。无对比期则全部省略。 */
  const cmpTailCells = (cv, pv) =>
    hasPrev
      ? [
          num2(pv.volume, fmtThousands2),
          pct(cv.volume, pv.volume),
          num2(pv.pnl, fmtThousands2),
          pct(cv.pnl, pv.pnl),
        ]
      : [];
  /** 对比尾列表头（与 cmpTailCells 对齐）。 */
  const cmpTailHeader = hasPrev
    ? ["成交量（7天前）", "成交量较7天前", "盈亏（7天前）", "盈亏较7天前"]
    : [];

  // ========== 核心指标 ==========
  ws.addRow([]);
  ws.addRow(["核心指标"]);
  ws.addRow(
    hasPrev ? ["指标", "当前区间", "对比区间", "变化（相对对比值）"] : ["指标", "当前区间"],
  );
  const addMetricRow = (label, curVal, prevVal, fmt) => {
    const cells = [label, num2(curVal, fmt)];
    if (hasPrev) cells.push(num2(prevVal, fmt), pct(curVal, prevVal));
    addFmtRow(cells);
  };
  addMetricRow("总成交量", cur.totalVolume, prev?.totalVolume, fmtThousands2);
  addMetricRow("总盈亏", cur.totalPnl, prev?.totalPnl, fmtThousands2);
  addMetricRow("盈亏 ROT（bps）", cur.rotBps, prev?.rotBps, fmtDec2);
  addMetricRow("总手续费（USDT）", cur.totalFee, prev?.totalFee, fmtThousands2);
  // Maker 占比用百分比格式（存小数）
  {
    const cells = [{ v: cur.makerRatioPct / 100, fmt: fmtPct }];
    if (hasPrev) {
      cells.push(
        { v: prev.makerRatioPct / 100, fmt: fmtPct },
        pct(cur.makerRatioPct, prev.makerRatioPct),
      );
    }
    addFmtRow(["总 Maker 占比", ...cells]);
  }

  // ========== 按交易所（各交易类型合并） ==========
  ws.addRow([]);
  ws.addRow(["按交易所（各交易类型合并）：先当期，后7天前同期"]);
  ws.addRow(["交易所", "成交量（当前）", "盈亏（当前）", ...cmpTailHeader]);
  if (sortedExchanges.length === 0) {
    ws.addRow(["（无交易所维度数据）"]);
  } else {
    for (const ex of sortedExchanges) {
      const cv = curEx.get(ex) || { volume: 0, pnl: 0 };
      const pv = prevEx.get(ex) || { volume: 0, pnl: 0 };
      addFmtRow([
        ex,
        num2(cv.volume, fmtThousands2),
        num2(cv.pnl, fmtThousands2),
        ...cmpTailCells(cv, pv),
      ]);
    }
  }

  // ========== 按交易所与交易类型 ==========
  ws.addRow([]);
  ws.addRow(["按交易所与交易类型：先当期，后7天前同期"]);
  ws.addRow(["交易所", "交易类型", "成交量（当前）", "盈亏（当前）", ...cmpTailHeader]);
  if (sortedExTt.length === 0) {
    ws.addRow(["（无交易所+交易类型维度数据）"]);
  } else {
    for (const k of sortedExTt) {
      const sep = k.indexOf(EXCHANGE_TRADE_TYPE_KEY_SEP);
      const ex = sep >= 0 ? k.slice(0, sep) : k;
      const tt = sep >= 0 ? k.slice(sep + 1) : "";
      const cv = curExTt.get(k) || { volume: 0, pnl: 0 };
      const pv = prevExTt.get(k) || { volume: 0, pnl: 0 };
      addFmtRow([
        ex,
        tradeTypeLabel(tt),
        num2(cv.volume, fmtThousands2),
        num2(cv.pnl, fmtThousands2),
        ...cmpTailCells(cv, pv),
      ]);
    }
  }

  // ========== 按业务线 / 策略类型（先页面合计，再按现货 / 合约拆解，含 ROT） ==========
  // 列顺序：维度 | 类型 | 成交量(当前) | 盈亏(当前) | 盈亏ROT | [成交量(7天前) | 成交量变化% | 盈亏(7天前) | 盈亏变化% | 对比ROT]
  const renderClassifiedBlock = (title, dimLabel, curMap, prevMap, emptyHint) => {
    ws.addRow([]);
    ws.addRow([title]);
    ws.addRow([
      dimLabel,
      "类型",
      "成交量（当前）",
      "盈亏（当前）",
      "盈亏 ROT（bps）",
      ...cmpTailHeader,
      ...(hasPrev ? ["对比 ROT（bps）"] : []),
    ]);
    const rows = buildClassifiedBreakdownRows(curMap, prevMap);
    if (rows.length === 0) {
      ws.addRow([emptyHint]);
      return;
    }
    for (const summaryRow of rows) {
      const cv = summaryRow.current;
      const pv = summaryRow.previous;
      const rot = cv.volume > 0 ? (cv.pnl / cv.volume) * 10000 : 0;
      const rotPrev = hasPrev && pv.volume > 0 ? (pv.pnl / pv.volume) * 10000 : null;
      const excelRow = addFmtRow([
        summaryRow.name,
        summaryRow.isTotal ? "合计" : tradeTypeLabel(summaryRow.tradeType),
        num2(cv.volume, fmtThousands2),
        num2(cv.pnl, fmtThousands2),
        num2(rot, fmtDec2),
        ...cmpTailCells(cv, pv),
        ...(hasPrev ? [rotPrev == null ? null : num2(rotPrev, fmtDec2)] : []),
      ]);
      if (summaryRow.isTotal) {
        excelRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { ...cell.font, bold: true };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFF6FF" } };
        });
      }
    }
  };
  renderClassifiedBlock(
    "按业务线：合计行，现货 / 合约行为拆解；先当期，后7天前同期",
    "业务线",
    curBl,
    prevBl,
    "（无业务线维度数据）",
  );
  renderClassifiedBlock(
    "按策略类型：合计行，现货 / 合约行为拆解；先当期，后7天前同期",
    "策略类型",
    curSt,
    prevSt,
    "（无策略类型维度数据）",
  );

  applySummarySheetLayout(ws);
}

function addObjectsSheet(wb, title, rows) {
  const name = ensureUniqueSheetName(wb, title);
  const ws = wb.addWorksheet(name);
  if (!rows?.length) {
    ws.addRow(["无数据"]);
    return;
  }
  const keySet = new Set();
  rows.forEach((r) => {
    if (r && typeof r === "object") Object.keys(r).forEach((k) => keySet.add(k));
  });
  const keys = [...keySet];
  if (!keys.length) {
    ws.addRow(["无数据"]);
    return;
  }
  ws.addRow(keys);
  rows.forEach((r) => {
    ws.addRow(
      keys.map((k) => {
        const v = r[k];
        if (v != null && typeof v === "object") return JSON.stringify(v);
        return v;
      }),
    );
  });
}

function addKvFromObject(wb, title, obj) {
  const name = ensureUniqueSheetName(wb, title);
  const ws = wb.addWorksheet(name);
  ws.addRow(["项", "值"]);
  if (!obj || typeof obj !== "object") {
    ws.addRow(["无数据", ""]);
    return;
  }
  Object.entries(obj).forEach(([k, v]) => {
    if (v != null && typeof v === "object") ws.addRow([k, JSON.stringify(v)]);
    else ws.addRow([k, v]);
  });
}

async function fetchAllAccountConfigs(ownerUserId, strictOwnerOnly = false) {
  const rows = [];
  let page = 1;
  let totalPages = 1;
  do {
    const base = { page, page_size: 200 };
    const params = withStrictOwnerOnly(withOwnerUserId(base, ownerUserId), strictOwnerOnly);
    const res = await apiService.accountConfigs.getConfigs(params);
    const payload = res.data?.data || {};
    const list = payload.list || [];
    rows.push(...list);
    totalPages = Number(payload.total_pages || 1);
    page += 1;
  } while (page <= totalPages);
  return rows;
}

function buildAccountConfigIndex(configs) {
  const byTriple = new Map();
  for (const row of configs || []) {
    const account = String(row.account || "").trim();
    const exchange = String(row.exchange || "").trim();
    const tradeType = String(row.trade_type || "").trim();
    if (!account || !exchange || !tradeType) continue;
    const key = `${account}${EXCHANGE_TRADE_TYPE_KEY_SEP}${exchange}${EXCHANGE_TRADE_TYPE_KEY_SEP}${tradeType}`;
    const configId = Number(row.id || 0);
    // 同三元组存在多条配置时保留 id 最小的一条，与后端分类统计 JOIN 的 MIN(id) 去重口径一致
    const existing = byTriple.get(key);
    if (existing && existing.configId > 0 && (configId <= 0 || existing.configId <= configId)) {
      continue;
    }
    byTriple.set(key, {
      configId,
      businessLineId: Number(row.business_line_id || row.business_line?.id || 0),
      businessLineName: String(row.business_line?.name || "").trim(),
      businessLineCode: String(row.business_line?.code || "").trim(),
      strategyTypeId: Number(row.strategy_type_id || row.strategy_type?.id || 0),
      strategyTypeName: String(row.strategy_type?.name || "").trim(),
      strategyTypeCode: String(row.strategy_type?.code || "").trim(),
    });
  }
  return byTriple;
}

function buildAccountTripleMetrics(tradeAccountPairs, equityAccounts) {
  const byTriple = new Map();
  for (const row of tradeAccountPairs || []) {
    const account = String(row.account || "").trim();
    const exchange = String(row.exchange || "").trim();
    const tradeType = String(row.trade_type || "").trim();
    if (!account || !exchange || !tradeType) continue;
    const key = `${account}${EXCHANGE_TRADE_TYPE_KEY_SEP}${exchange}${EXCHANGE_TRADE_TYPE_KEY_SEP}${tradeType}`;
    if (!byTriple.has(key)) {
      byTriple.set(key, { account, exchange, tradeType, volume: 0, pnl: 0 });
    }
    byTriple.get(key).volume += Number(row.total_volume || 0);
  }
  for (const row of equityAccounts || []) {
    const account = String(row.account || "").trim();
    const exchange = String(row.exchange || "").trim();
    const tradeType = String(row.trade_type || "").trim();
    if (!account || !exchange || !tradeType) continue;
    const key = `${account}${EXCHANGE_TRADE_TYPE_KEY_SEP}${exchange}${EXCHANGE_TRADE_TYPE_KEY_SEP}${tradeType}`;
    if (!byTriple.has(key)) {
      byTriple.set(key, { account, exchange, tradeType, volume: 0, pnl: 0 });
    }
    byTriple.get(key).pnl += (row.trends || []).reduce((sum, t) => sum + Number(t.pnl || 0), 0);
  }
  return byTriple;
}

function aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, dimension, hedgeGroups = []) {
  const hedgeMap = new Map(); // triple -> groupName
  for (const hg of hedgeGroups) {
    // member_triples 为结构化成员三元组；hg.members 是给「对冲汇总」表展示用的拼接字符串，不能用于匹配
    for (const m of hg.member_triples || []) {
      const k = `${String(m.account).trim()}${EXCHANGE_TRADE_TYPE_KEY_SEP}${String(m.exchange).trim()}${EXCHANGE_TRADE_TYPE_KEY_SEP}${String(m.trade_type).trim()}`;
      hedgeMap.set(k, hg.group_name || hg.name);
    }
  }
  const totalMap = new Map();
  const exchangeMap = new Map();
  const exchangeTypeMap = new Map();
  const dimFields = dimension === "business_line"
    ? { id: "businessLineId", name: "businessLineName", code: "businessLineCode" }
    : { id: "strategyTypeId", name: "strategyTypeName", code: "strategyTypeCode" };

  for (const [key, metric] of accountTripleMetrics.entries()) {
    const cfg = configByTriple.get(key);
    const dimId = Number(cfg?.[dimFields.id] || 0);
    const dimName = String(cfg?.[dimFields.name] || "").trim() || "未分配";
    const dimCode = String(cfg?.[dimFields.code] || "").trim();

    const totalKey = String(dimId);
    if (!totalMap.has(totalKey)) {
      totalMap.set(totalKey, {
        dimension_id: dimId,
        dimension_name: dimName,
        dimension_code: dimCode,
        exchange: "",
        trade_type: "",
        volume: 0,
        pnl: 0,
      });
    }
    const totalRow = totalMap.get(totalKey);
    totalRow.volume += metric.volume;
    totalRow.pnl += metric.pnl;

    const exKey = `${totalKey}${EXCHANGE_TRADE_TYPE_KEY_SEP}${metric.exchange}`;
    if (!exchangeMap.has(exKey)) {
      exchangeMap.set(exKey, {
        dimension_id: dimId,
        dimension_name: dimName,
        dimension_code: dimCode,
        exchange: metric.exchange,
        trade_type: "",
        volume: 0,
        pnl: 0,
      });
    }
    const exRow = exchangeMap.get(exKey);
    exRow.volume += metric.volume;
    exRow.pnl += metric.pnl;

    const exTypeKey = `${exKey}${EXCHANGE_TRADE_TYPE_KEY_SEP}${metric.tradeType}`;
    if (!exchangeTypeMap.has(exTypeKey)) {
      exchangeTypeMap.set(exTypeKey, {
        dimension_id: dimId,
        dimension_name: dimName,
        dimension_code: dimCode,
        exchange: metric.exchange,
        trade_type: metric.tradeType,
        volume: 0,
        pnl: 0,
      });
    }
    const exTypeRow = exchangeTypeMap.get(exTypeKey);
    exTypeRow.volume += metric.volume;
    exTypeRow.pnl += metric.pnl;
    
    // 标记是否为对冲账号
    if (hedgeMap.has(key)) {
      exTypeRow.hedge_group = hedgeMap.get(key);
    }
  }

  const sorter = (a, b) => {
    if (b.volume !== a.volume) return b.volume - a.volume;
    const dc = String(a.dimension_name).localeCompare(String(b.dimension_name), "zh-CN");
    if (dc !== 0) return dc;
    const ec = String(a.exchange).localeCompare(String(b.exchange), "zh-CN");
    if (ec !== 0) return ec;
    return String(a.trade_type).localeCompare(String(b.trade_type), "zh-CN");
  };

  return {
    total: [...totalMap.values()].sort(sorter),
    exchange: [...exchangeMap.values()].sort(sorter),
    exchangeType: [...exchangeTypeMap.values()].sort(sorter),
  };
}

function addClassifiedBreakdownSheet(wb, title, rows, dimensionLabel) {
  const name = ensureUniqueSheetName(wb, title);
  const ws = wb.addWorksheet(name);
  ws.addRow([`${dimensionLabel}分层汇总`]);
  ws.addRow(["层级", dimensionLabel, "代码", "交易所", "交易类型", "对冲组", "成交量", "盈亏", "盈亏ROT(bps)"]);

  const pushRows = (level, list) => {
    for (const row of list || []) {
      const volume = Number(row.volume || 0);
      const pnl = Number(row.pnl || 0);
      const rotBps = volume > 0 ? (pnl / volume) * 10000 : 0;
      const excelRow = ws.addRow([
        level,
        row.dimension_name || "未分配",
        row.dimension_code || "",
        row.exchange || "",
        row.trade_type || "",
        row.hedge_group || "—",
        Number(volume.toFixed(2)),
        Number(pnl.toFixed(2)),
        Number(rotBps.toFixed(2)),
      ]);
      excelRow.getCell(7).numFmt = "#,##0.00";
      excelRow.getCell(8).numFmt = "#,##0.00";
      excelRow.getCell(9).numFmt = "0.00";
    }
  };

  pushRows("总计", rows?.total);
  pushRows("交易所", rows?.exchange);
  pushRows("交易所+类型", rows?.exchangeType);

  if (!(rows?.total?.length || rows?.exchange?.length || rows?.exchangeType?.length)) {
    ws.addRow(["无数据"]);
  }
}

function addHedgeSummarySheet(wb, items) {
  const name = ensureUniqueSheetName(wb, "对冲汇总");
  const ws = wb.addWorksheet(name);
  if (!items?.length) {
    ws.addRow(["无对冲组数据或无权限"]);
    return;
  }

  const totalVolume = items.reduce((sum, item) => sum + Number(item.period_total_volume || 0), 0);
  const totalPnl = items.reduce((sum, item) => sum + Number(item.total_pnl || 0), 0);
  const totalNetAssets = items.reduce((sum, item) => sum + Number(item.latest_combined_net_assets || 0), 0);
  const avgEffValues = items.map((item) => Number(item.avg_hedge_efficiency || 0));
  const avgEff = avgEffValues.length
    ? avgEffValues.reduce((sum, v) => sum + v, 0) / avgEffValues.length
    : 0;

  ws.addRow(["对冲总数据"]);
  ws.addRow(["对冲组数量", items.length]);
  ws.addRow(["区间总交易量", Number(totalVolume.toFixed(2))]);
  ws.addRow(["区间总盈亏", Number(totalPnl.toFixed(2))]);
  ws.addRow(["期末组合净资产合计", Number(totalNetAssets.toFixed(2))]);
  ws.addRow(["平均对冲效率", Number(avgEff.toFixed(2))]);
  ws.addRow([]);
  ws.addRow([
    "对冲组",
    "成员",
    "区间总交易量",
    "区间总盈亏",
    "期末组合净资产",
    "平均对冲效率",
    "最大回撤",
    "最大回撤比例",
    "胜率",
  ]);

  for (const item of items) {
    const row = ws.addRow([
      item.group_name,
      item.members,
      Number(Number(item.period_total_volume || 0).toFixed(2)),
      Number(Number(item.total_pnl || 0).toFixed(2)),
      Number(Number(item.latest_combined_net_assets || 0).toFixed(2)),
      Number(Number(item.avg_hedge_efficiency || 0).toFixed(2)),
      Number(Number(item.max_drawdown || 0).toFixed(2)),
      Number(Number(item.max_drawdown_percent || 0).toFixed(2)),
      Number(Number(item.win_rate || 0).toFixed(2)),
    ]);
    for (const col of [3, 4, 5, 6, 7, 8, 9]) {
      row.getCell(col).numFmt = "#,##0.00";
    }
  }
}

function flattenEquityAccounts(accounts) {
  const out = [];
  (accounts || []).forEach((acc) => {
    (acc.trends || []).forEach((t) => {
      out.push({
        account: acc.account,
        exchange: acc.exchange,
        trade_type: acc.trade_type,
        date: t.equity_date ?? t.date,
        net_assets: t.net_assets,
        pnl: t.pnl,
      });
    });
  });
  return out;
}

function flattenEquityExchanges(rows) {
  const out = [];
  (rows || []).forEach((row) => {
    const ex = row.exchange;
    const tt = row.trade_type;
    (row.trends || []).forEach((t) => {
      out.push({
        exchange: ex,
        trade_type: tt,
        date: t.equity_date ?? t.date,
        net_assets: t.net_assets,
        pnl: t.pnl,
      });
    });
  });
  return out;
}

/** 业务线 / 策略类型维度权益日度明细（与列表接口结构一致） */
function flattenEquityClassified(rows) {
  const out = [];
  (rows || []).forEach((row) => {
    const id = row.id;
    const name = row.name ?? "";
    const code = row.code ?? "";
    (row.trends || []).forEach((t) => {
      out.push({
        dimension_id: id,
        dimension_name: name,
        dimension_code: code,
        date: t.equity_date ?? t.date,
        net_assets: t.net_assets,
        pnl: t.pnl,
      });
    });
  });
  return out;
}

async function fetchTradeBundle(dateFrom, dateTo, ownerUserId, strictOwnerOnly = false) {
  const params = withStrictOwnerOnly(
    withOwnerUserId({ date_from: dateFrom, date_to: dateTo }, ownerUserId),
    strictOwnerOnly,
  );
  const dailyParams = { ...params, limit: 500 };
  const [statsRes, dailyRes, accPairRes, pairRes, exRes] = await Promise.all([
    apiService.trades.getTradeStats(params),
    apiService.trades.getDailyStats(dailyParams),
    apiService.trades.getAccountPairTrendData(params),
    apiService.trades.getPairTrendData(params),
    apiService.trades.getExchangeDailyTrendData(params),
  ]);
  return {
    stats: statsRes.data?.data,
    daily: dailyRes.data?.data || [],
    accountPairs: accPairRes.data?.data || [],
    pairs: pairRes.data?.data || [],
    exchangeDaily: exRes.data?.data || [],
  };
}

async function fetchEquityBundle(
  dateFrom,
  dateTo,
  ownerUserId,
  includeUnassignedClassified = true,
  strictOwnerOnly = false,
) {
  const params = withStrictOwnerOnly(
    withOwnerUserId({ date_from: dateFrom, date_to: dateTo }, ownerUserId),
    strictOwnerOnly,
  );
  const [totalRes, accRes, exRes, blRes, stRes] = await Promise.all([
    apiService.equity.getTotalTrends(params),
    apiService.equity.getAccountTrends(params),
    apiService.equity.getExchangeTrends(params),
    apiService.equity.getBusinessLineTrends(params),
    apiService.equity.getStrategyTypeTrends(params),
  ]);
  return {
    total: totalRes.data?.data || [],
    accounts: accRes.data?.data || [],
    exchanges: exRes.data?.data || [],
    businessLines: filterClassifiedDimensionRows(
      blRes.data?.data || [],
      includeUnassignedClassified,
    ),
    strategyTypes: filterClassifiedDimensionRows(
      stRes.data?.data || [],
      includeUnassignedClassified,
    ),
  };
}

async function addHedgeSheets(wb, dateFrom, dateTo, ownerUserId, strictOwnerOnly = false) {
  let list = [];
  try {
    const listParams = withStrictOwnerOnly(withOwnerUserId({}, ownerUserId), strictOwnerOnly);
    const gr = await apiService.hedgeGroups.getGroups(listParams);
    list = gr.data?.data || [];
  } catch (e) {
    if (e.response?.status === 403) return [];
    throw e;
  }
  if (!list.length) {
    const ws = wb.addWorksheet(ensureUniqueSheetName(wb, "对冲组"));
    ws.addRow(["无对冲组数据或无权限"]);
    return [];
  }
  const params = withStrictOwnerOnly(
    withOwnerUserId({ date_from: dateFrom, date_to: dateTo }, ownerUserId),
    strictOwnerOnly,
  );
  const summaryItems = [];
  for (const g of list) {
    let payload;
    try {
      const tr = await apiService.hedgeGroups.getGroupTrends(g.id, params);
      payload = tr.data?.data;
    } catch {
      continue;
    }
    const base = `对冲_${g.name || g.id}`;
    addObjectsSheet(wb, `${base}_组合净值`, payload?.combined || []);
    addObjectsSheet(wb, `${base}_成交量`, payload?.volume_trend || []);
    if (payload?.analysis && typeof payload.analysis === "object") {
      addKvFromObject(wb, `${base}_区间分析`, payload.analysis);
    }
    const members = payload?.members || [];
    const analysis = payload?.analysis || {};
    summaryItems.push({
      group_name: payload?.group?.name || g.name || String(g.id),
      members: members
        .map((m) => `${m.account}/${m.exchange}/${m.trade_type}${m.role ? `(${m.role})` : ""}`)
        .join(" | "),
      member_triples: members.map((m) => ({
        account: m.account,
        exchange: m.exchange,
        trade_type: m.trade_type,
      })),
      period_total_volume: Number(analysis.period_total_volume || 0),
      total_pnl: Number(analysis.total_pnl || 0),
      latest_combined_net_assets: Number(analysis.latest_combined_net_assets || 0),
      avg_hedge_efficiency: Number(analysis.avg_hedge_efficiency || 0),
      max_drawdown: Number(analysis.max_drawdown || 0),
      max_drawdown_percent: Number(analysis.max_drawdown_percent || 0),
      win_rate: Number(analysis.win_rate || 0),
    });
    members.forEach((m, idx) => {
      const flat = (m.trends || []).map((t) => ({
        account: m.account,
        exchange: m.exchange,
        trade_type: m.trade_type,
        role: m.role,
        date: t.date,
        net_assets: t.net_assets,
        pnl: t.pnl,
      }));
      addObjectsSheet(wb, `${base}_成员${idx + 1}`, flat);
    });
  }
  return summaryItems;
}

export async function exportTradeDataPanoramaExcel({
  dateFrom,
  dateTo,
  ownerUserId,
}) {
  const { strictOwnerOnly, includeUnassignedClassified } =
    resolvePanoramaExportDataScope(ownerUserId);
  const wb = new ExcelJS.Workbook();
  wb.creator = "trade_volume";
  addMetaSheet(wb, {
    pageTitle: "交易数据全景分析",
    dateFrom,
    dateTo,
    ownerUserId,
  });
  await addExportSummarySheet(wb, {
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified,
    strictOwnerOnly,
  });
  const accountConfigs = await fetchAllAccountConfigs(ownerUserId, strictOwnerOnly);
  const configByTriple = buildAccountConfigIndex(accountConfigs);
  const bundle = await fetchTradeBundle(dateFrom, dateTo, ownerUserId, strictOwnerOnly);
  const eq = await fetchEquityBundle(
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified,
    strictOwnerOnly,
  );
  const accountTripleMetrics = buildAccountTripleMetrics(bundle.accountPairs, eq.accounts);
  const hedgeSummary = await addHedgeSheets(wb, dateFrom, dateTo, ownerUserId, strictOwnerOnly);
  addHedgeSummarySheet(wb, hedgeSummary);

  addClassifiedBreakdownSheet(
    wb,
    "业务线分层汇总",
    aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, "business_line", hedgeSummary),
    "业务线",
  );
  addClassifiedBreakdownSheet(
    wb,
    "策略类型分层汇总",
    aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, "strategy_type", hedgeSummary),
    "策略类型",
  );
  addKvFromObject(wb, "交易量汇总", bundle.stats || {});
  addObjectsSheet(wb, "日度统计", bundle.daily);
  addObjectsSheet(wb, "账号交易对趋势", bundle.accountPairs);
  addObjectsSheet(wb, "交易对趋势", bundle.pairs);
  addObjectsSheet(wb, "交易所日度", bundle.exchangeDaily);
  const buf = await wb.xlsx.writeBuffer();
  await downloadBuffer(buf, `交易数据全景分析_${dateFrom}_${dateTo}.xlsx`);
}

export async function exportCompositeOverviewExcel({
  dateFrom,
  dateTo,
  ownerUserId,
}) {
  const { strictOwnerOnly, includeUnassignedClassified } =
    resolvePanoramaExportDataScope(ownerUserId);
  const wb = new ExcelJS.Workbook();
  wb.creator = "trade_volume";
  addMetaSheet(wb, {
    pageTitle: "综合分析全景",
    dateFrom,
    dateTo,
    ownerUserId,
  });
  await addExportSummarySheet(wb, {
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified,
    strictOwnerOnly,
  });
  const accountConfigs = await fetchAllAccountConfigs(ownerUserId, strictOwnerOnly);
  const configByTriple = buildAccountConfigIndex(accountConfigs);
  const eq = await fetchEquityBundle(
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified,
    strictOwnerOnly,
  );
  addObjectsSheet(wb, "权益总览", eq.total);
  addObjectsSheet(wb, "权益分账户明细", flattenEquityAccounts(eq.accounts));
  addObjectsSheet(wb, "权益分交易所明细", flattenEquityExchanges(eq.exchanges));
  addObjectsSheet(wb, "权益分业务线明细", flattenEquityClassified(eq.businessLines));
  addObjectsSheet(wb, "权益分策略类型明细", flattenEquityClassified(eq.strategyTypes));
  const bundle = await fetchTradeBundle(dateFrom, dateTo, ownerUserId, strictOwnerOnly);
  const accountTripleMetrics = buildAccountTripleMetrics(bundle.accountPairs, eq.accounts);
  const hedgeSummary = await addHedgeSheets(wb, dateFrom, dateTo, ownerUserId, strictOwnerOnly);
  addHedgeSummarySheet(wb, hedgeSummary);

  addClassifiedBreakdownSheet(
    wb,
    "业务线分层汇总",
    aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, "business_line", hedgeSummary),
    "业务线",
  );
  addClassifiedBreakdownSheet(
    wb,
    "策略类型分层汇总",
    aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, "strategy_type", hedgeSummary),
    "策略类型",
  );
  addKvFromObject(wb, "交易量汇总", bundle.stats || {});
  addObjectsSheet(wb, "日度统计", bundle.daily);
  addObjectsSheet(wb, "账号交易对趋势", bundle.accountPairs);
  addObjectsSheet(wb, "交易对趋势", bundle.pairs);
  addObjectsSheet(wb, "交易所日度", bundle.exchangeDaily);
  const buf = await wb.xlsx.writeBuffer();
  await downloadBuffer(buf, `综合分析全景_${dateFrom}_${dateTo}.xlsx`);
}

export async function exportFundTrendsExcel({
  dateFrom,
  dateTo,
  ownerUserId,
}) {
  const { strictOwnerOnly, includeUnassignedClassified } =
    resolvePanoramaExportDataScope(ownerUserId);
  const wb = new ExcelJS.Workbook();
  wb.creator = "trade_volume";
  addMetaSheet(wb, {
    pageTitle: "资金与盈亏走势",
    dateFrom,
    dateTo,
    ownerUserId,
  });
  await addExportSummarySheet(wb, {
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified,
    strictOwnerOnly,
  });
  const accountConfigs = await fetchAllAccountConfigs(ownerUserId, strictOwnerOnly);
  const configByTriple = buildAccountConfigIndex(accountConfigs);
  const eq = await fetchEquityBundle(
    dateFrom,
    dateTo,
    ownerUserId,
    includeUnassignedClassified,
    strictOwnerOnly,
  );
  addObjectsSheet(wb, "总净值走势", eq.total);
  addObjectsSheet(wb, "分账户明细", flattenEquityAccounts(eq.accounts));
  addObjectsSheet(wb, "分交易所明细", flattenEquityExchanges(eq.exchanges));
  addObjectsSheet(wb, "分业务线明细", flattenEquityClassified(eq.businessLines));
  addObjectsSheet(wb, "分策略类型明细", flattenEquityClassified(eq.strategyTypes));
  const tradeBundle = await fetchTradeBundle(dateFrom, dateTo, ownerUserId, strictOwnerOnly);
  const accountTripleMetrics = buildAccountTripleMetrics(tradeBundle.accountPairs, eq.accounts);
  const hedgeSummary = await addHedgeSheets(wb, dateFrom, dateTo, ownerUserId, strictOwnerOnly);
  addHedgeSummarySheet(wb, hedgeSummary);

  addClassifiedBreakdownSheet(
    wb,
    "业务线分层汇总",
    aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, "business_line", hedgeSummary),
    "业务线",
  );
  addClassifiedBreakdownSheet(
    wb,
    "策略类型分层汇总",
    aggregateClassifiedVenueBreakdown(accountTripleMetrics, configByTriple, "strategy_type", hedgeSummary),
    "策略类型",
  );
  const buf = await wb.xlsx.writeBuffer();
  await downloadBuffer(buf, `资金与盈亏走势_${dateFrom}_${dateTo}.xlsx`);
}
